import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { datadogRum } from '@datadog/browser-rum';
import { Text, Button, Stack, Input } from 'rsuite';
import { Stripe } from '@stripe/stripe-js';
import {
  PresentationCol,
  PresentationGrid,
  PresentationRow,
} from '../../../../layout/PresentationLayout';
import { buildPaymentApi } from '../../../../../api';
import { resolvePaymentErrorMessage } from '../../../../../api/payment/paymentErrors';
import {
  STRIPE_NO_PAYMENT_METHOD_ATTACHED_INTENT_STATUSES,
  STRIPE_PAYMENT_INTENT_STATUSES,
} from '../../stripeConstants';
import {
  ACCOUNT_PICKER_CANCELLED_ALERT_MSG,
  ACCOUNT_PICKER_ERROR_ALERT_MSG,
  PAY_ACH_ERROR,
} from '../../../constants';
import { useSelector } from 'react-redux';
import {
  selectCurrentIDToken,
  selectCurrentUserEmail,
} from '../../../../../store/slices/auth/authSelectors';
import { selectPciPaymentApiEnabled } from '../../../../../store/slices/config/configSelectors';
import {
  selectPaymentFees,
  selectPaymentID,
  selectPaymentSignatory,
  selectSelectedPaymentMethod,
} from '../../../../../store/slices/payment/paymentSelectors';
import Notice from '../../../../user-feedback/Notice';
import IconIndicator from '../../../../user-feedback/IconIndicator';
import { convertNumberToCurrency } from '../../../../../common/utils';
import { calculateTotalDue } from '../../../common/utils';
import { getPaymentCategory } from '../../../../../common/paymentUtils';
import { FLOWS_ENUM } from '../../../../../common/enums';
import { BankAccountDetails, StripeBankAccountPaymentMethod } from '../types';

interface AccountHolderFormProps {
  flow: FLOWS_ENUM;
  setBankAccountDetails: (details: BankAccountDetails) => void;
  setClientSecret: (secret?: string) => void;
  stripe: Stripe | null;
}

export const AccountHolderForm = ({
  flow,
  setBankAccountDetails,
  setClientSecret,
  stripe,
}: AccountHolderFormProps) => {
  const [accountHolderName, setAccountHolderName] = useState('');
  const [accountHolderEmail, setAccountHolderEmail] = useState('');
  const [paymentInProgress, setPaymentsInProgress] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const currentUserEmail = useSelector(selectCurrentUserEmail);
  const authToken = useSelector(selectCurrentIDToken);
  const pciEnabled = useSelector(selectPciPaymentApiEnabled);
  const paymentID = useSelector(selectPaymentID);
  const paymentFees = useSelector(selectPaymentFees);
  const apiAccess = useMemo(() => buildPaymentApi(pciEnabled), [pciEnabled]);
  const selectedPaymentMethod = useSelector(selectSelectedPaymentMethod);
  const signatoryName = useSelector(selectPaymentSignatory);

  const totalDue = useMemo(
    () => calculateTotalDue(selectedPaymentMethod, paymentFees),
    [selectedPaymentMethod, paymentFees]
  );

  useEffect(() => {
    setAccountHolderEmail(currentUserEmail ?? '');
  }, [currentUserEmail]);

  useEffect(() => {
    setAccountHolderName(signatoryName ?? '');
  }, [signatoryName]);

  const payButtonDisabled = useMemo(
    () => paymentInProgress || !stripe,
    [paymentInProgress, stripe]
  );

  const paymentClick = useCallback(async () => {
    setErrorMessage('');
    setPaymentsInProgress(true);
    try {
      const { clientSecret } = await apiAccess.payByACHDirectDebit(
        {
          email: currentUserEmail ?? '',
          payment_category: getPaymentCategory(flow)!,
          payment_id: Number(paymentID),
          signatory: signatoryName ?? '',
          subtotal: paymentFees?.summary?.total ?? 0,
          signature: paymentFees?.signature ?? '',
          credits: paymentFees?.summary?.credit ?? 0,
        },
        authToken
      );

      const { paymentIntent, error } =
        await stripe!.collectBankAccountForPayment({
          clientSecret,
          params: {
            payment_method_type: 'us_bank_account',
            payment_method_data: {
              billing_details: {
                name: accountHolderName,
                email: accountHolderEmail.trim(),
              },
            },
          },
          expand: ['payment_method'],
        });

      // Without a client secret the confirmation step cannot be rendered, so
      // this only counts as a transition when Stripe actually returned one.
      const collectedClientSecret = paymentIntent?.client_secret ?? undefined;

      if (
        !error &&
        paymentIntent?.status ===
          STRIPE_PAYMENT_INTENT_STATUSES.REQUIRES_CONFIRMATION &&
        collectedClientSecret
      ) {
        const pm =
          paymentIntent?.payment_method as unknown as StripeBankAccountPaymentMethod;
        const {
          account_number,
          routing_number,
          account_type,
          bank_name,
          last4,
        } = pm?.us_bank_account ?? {};

        const { name } = pm?.billing_details ?? {};

        setBankAccountDetails({
          accountHolder: name,
          accountNumber: account_number,
          accountType: account_type,
          bankName: bank_name,
          last4,
          routingNumber: routing_number,
        });
        // Leaves the button disabled: setting the client secret swaps this
        // form out for the confirmation step.
        setClientSecret(collectedClientSecret);
      } else if (
        STRIPE_NO_PAYMENT_METHOD_ATTACHED_INTENT_STATUSES.includes(
          paymentIntent?.status ?? ''
        )
      ) {
        setErrorMessage(ACCOUNT_PICKER_CANCELLED_ALERT_MSG);
        setPaymentsInProgress(false);
      } else {
        setErrorMessage(ACCOUNT_PICKER_ERROR_ALERT_MSG);
        setPaymentsInProgress(false);
      }
    } catch (err) {
      setErrorMessage(resolvePaymentErrorMessage(err, PAY_ACH_ERROR));
      datadogRum.addError(err);
      setPaymentsInProgress(false);
    }
  }, [
    accountHolderEmail,
    accountHolderName,
    apiAccess,
    currentUserEmail,
    flow,
    paymentID,
    authToken,
    paymentFees?.signature,
    paymentFees?.summary?.total,
    paymentFees?.summary?.credit,
    setBankAccountDetails,
    setClientSecret,
    signatoryName,
    stripe,
  ]);

  return (
    <>
      <PresentationGrid fluid>
        <PresentationRow className="mb-16">
          <PresentationCol xs={24}>
            <Stack
              alignItems="flex-start"
              direction="column"
              spacing={16}
            >
              <Stack.Item>
                <Text className="text-small">
                  To make a secure payment, connect to your bank account and
                  verify your identity.
                </Text>
              </Stack.Item>
              <Stack.Item>
                <Text className="text-small">
                  Please ensure that the name provided matches the name on file
                  for the bank account and disable any active pop-up blockers as
                  they could interfere with the payment process.
                </Text>
              </Stack.Item>
            </Stack>
          </PresentationCol>
        </PresentationRow>
        <PresentationRow className="mb-16">
          <PresentationCol xs={24}>
            <Text
              className="text-small mb-4"
              id="label-ach-account-holder-name"
            >
              Account Holder&apos;s Full Name
            </Text>
            <Input
              aria-labelledby="label-ach-account-holder-name"
              autoComplete="name"
              id="ach-account-holder-name"
              onChange={setAccountHolderName}
              value={accountHolderName}
            />
          </PresentationCol>
        </PresentationRow>
        <PresentationRow className="mb-16">
          <PresentationCol xs={24}>
            <Text
              className="text-small mb-4"
              id="label-ach-account-holder-email"
            >
              Email
            </Text>
            <Input
              aria-labelledby="label-ach-account-holder-email"
              autoComplete="email"
              id="ach-account-holder-email"
              onChange={setAccountHolderEmail}
              value={accountHolderEmail}
            />
          </PresentationCol>
        </PresentationRow>
        {errorMessage && (
          <PresentationRow>
            <PresentationCol xs={24}>
              <Notice message={errorMessage} />
            </PresentationCol>
          </PresentationRow>
        )}
        <PresentationRow>
          <PresentationCol xs={24}>
            {paymentInProgress && <IconIndicator />}
            <Button
              className="btn-primary full-width"
              disabled={payButtonDisabled}
              onClick={paymentClick}
            >
              Pay {convertNumberToCurrency(totalDue)}
            </Button>
          </PresentationCol>
        </PresentationRow>
      </PresentationGrid>
    </>
  );
};

export default AccountHolderForm;
